import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useI18n } from "@expertloop/i18n/react";
import { FileViewerModal, canPreviewFile, type ViewerFile } from "@expertloop/web-viewer";
import type { AdminFileAccess, AdminMediaFile, AdminMediaVideo, AdminVideoAccess } from "@expertloop/shared";
import { adminApi } from "../../services/api";
import { ADMIN_API_BASE_URL } from "../../services/config";

export interface AttachmentViewer {
	openFile: (file: AdminMediaFile) => void;
	openVideo: (video: AdminMediaVideo) => void;
	/** Identifiant de la pièce dont le lien est en cours de demande. */
	busyId: string | null;
	/** À rendre une fois dans la page : le lecteur ouvert, ou le message d'échec. */
	viewer: ReactNode;
}

/**
 * Ouvre une pièce de la conversation pour le support, dans le lecteur des parties (PDF, image,
 * audio, vidéo, texte). Le lien est demandé à l'ouverture, signé pour quelques minutes et
 * journalisé : le support ne se connecte pas « en tant que » une partie. Une pièce qu'aucun
 * lecteur ne sait afficher s'ouvre dans un nouvel onglet.
 */
export function useAttachmentViewer(chatId: string): AttachmentViewer {
	const { t } = useI18n();
	const [file, setFile] = useState<ViewerFile | null>(null);
	const [video, setVideo] = useState<{ embedUrl: string } | null>(null);
	const [busyId, setBusyId] = useState<string | null>(null);
	const [error, setError] = useState("");

	const openFile = useCallback(
		async (target: AdminMediaFile) => {
			setError("");
			setBusyId(target.id);
			try {
				const access = await adminApi.get<AdminFileAccess>(`/admin/chats/${chatId}/files/${target.id}/url`);
				if (canPreviewFile(access.mimeType)) {
					// Lecture par le relais de l'API : le stockage n'autorise que les origines des parties
					setFile({ id: target.id, originalFilename: access.filename, mimeType: access.mimeType, size: access.size, downloadUrl: `${ADMIN_API_BASE_URL}${access.contentPath}` });
				} else {
					window.open(access.url, "_blank", "noopener,noreferrer");
				}
			} catch {
				setError(t("adminChatDetail.media.openFailed"));
			} finally {
				setBusyId(null);
			}
		},
		[chatId, t],
	);

	const openVideo = useCallback(
		async (target: AdminMediaVideo) => {
			setError("");
			setBusyId(target.id);
			try {
				const access = await adminApi.get<AdminVideoAccess>(`/admin/chats/${chatId}/videos/${target.id}/playback`);
				setVideo({ embedUrl: access.embedUrl });
			} catch {
				setError(t("adminChatDetail.media.videoUnavailable"));
			} finally {
				setBusyId(null);
			}
		},
		[chatId, t],
	);

	const closeVideo = useCallback(() => setVideo(null), []);
	useEffect(() => {
		if (!video) return undefined;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") closeVideo();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [video, closeVideo]);

	const viewer = (
		<>
			{error && (
				<div className="media-error" role="alert">
					{error}
					<button type="button" className="btn btn-sm btn-outline" onClick={() => setError("")}>
						{t("adminChatDetail.media.close")}
					</button>
				</div>
			)}
			{file && <FileViewerModal file={file} onClose={() => setFile(null)} />}
			{video && (
				<div className="modal-overlay" onClick={closeVideo}>
					<div className="modal-content media-video-modal" role="dialog" aria-modal="true" aria-label={t("adminChatDetail.media.videoTitle")} onClick={(event) => event.stopPropagation()}>
						<div className="modal-header">
							<h2>{t("adminChatDetail.media.videoTitle")}</h2>
							<button type="button" className="modal-close" aria-label={t("adminChatDetail.media.close")} onClick={closeVideo}>
								&times;
							</button>
						</div>
						<div className="modal-body">
							<iframe title={t("adminChatDetail.media.videoTitle")} src={video.embedUrl} allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture" allowFullScreen className="media-video-frame" />
						</div>
					</div>
				</div>
			)}
		</>
	);

	return { openFile: (target) => void openFile(target), openVideo: (target) => void openVideo(target), busyId, viewer };
}
